import { memo } from "react";
import { BdcShipmentHeader } from "../../../types/bdc.type";
import { ControlColumn, ControlSheet } from "../controlSheet";
import { lineText, pcs, shortSkuName, thaiDate } from "../format";

const qtyCell = (value: number, key: number) =>
  value ? (
    <td key={key} className="q">
      {value}
    </td>
  ) : (
    <td key={key} className="q z">
      ·
    </td>
  );

const ColumnHead = ({ columns }: { columns: ControlColumn[] }) => (
  <tr>
    <th>จุดส่ง</th>
    <th>รหัส</th>
    <th>ร้าน</th>
    {columns.map((column) => (
      <th key={column.barcode} className="sk" title={`${column.item_code} ${column.name}`}>
        {shortSkuName(column.name)}
      </th>
    ))}
    <th className="n">ชิ้น</th>
  </tr>
);

export const controlSheetTitle = (header: BdcShipmentHeader) =>
  `ใบควบคุมสินค้า · ${header.dc_code} ${header.dc_name} · รอบ ${header.round ?? "-"} · ส่ง ${thaiDate(header.delivery_date)} · PO ${header.po_number}`;

const TotalRows = ({ sheet }: { sheet: ControlSheet }) => (
  <>
    <tr className="gt">
      <td colSpan={3}>รวมทั้งหมด ({pcs(sheet.storeCount)} ร้าน)</td>
      {sheet.grandTotal.map(qtyCell)}
      <td className="q">{pcs(sheet.grandTotalPieces)}</td>
    </tr>
    <tr className="cr">
      <td colSpan={3}>ลังเต็ม</td>
      {sheet.fullCases.map((value, i) => (
        <td key={i} className="q">
          {value}
        </td>
      ))}
      <td className="q">{pcs(sheet.fullCases.reduce((a, b) => a + b, 0))}</td>
    </tr>
    <tr className="cr">
      <td colSpan={3}>เศษ (ชิ้น)</td>
      {sheet.remainder.map((value, i) => (
        <td key={i} className="q">
          {value}
        </td>
      ))}
      <td />
    </tr>
  </>
);

/**
 * The control-sheet pivot. On screen: one table, a <tbody> per สาย, sticky SKU heads.
 * In print: one table per สาย with its own repeated head and a page break after it, then the
 * totals on the last page — the way the warehouse hands one สาย to one loader.
 */
function ControlSheetView({
  sheet,
  header,
  print = false,
}: Readonly<{ sheet: ControlSheet; header: BdcShipmentHeader; print?: boolean }>) {
  const width = sheet.columns.length + 4;

  if (print) {
    const title = controlSheetTitle(header);
    return (
      <>
        {sheet.lines.map((line) => (
          <div key={String(line.line_no)} className="bdc-print-block">
            <table className="bdc-t bdc-pv">
              <thead>
                <tr>
                  <th colSpan={width} className="title">
                    {title} · สาย {lineText(line.line_no)} ({line.rows.length} ร้าน)
                  </th>
                </tr>
                <ColumnHead columns={sheet.columns} />
              </thead>
              <tbody>
                {line.rows.map((row) => (
                  <tr key={row.store_code}>
                    <td className="q">{lineText(row.stop_no)}</td>
                    <td className="mono">{row.store_code}</td>
                    <td>{row.store_name}</td>
                    {row.cells.map(qtyCell)}
                    <td className="q">
                      <b>{row.total}</b>
                    </td>
                  </tr>
                ))}
                <tr className="ls">
                  <td colSpan={3}>รวมสาย {lineText(line.line_no)}</td>
                  {line.subtotal.map(qtyCell)}
                  <td className="q">{pcs(line.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
        <div>
          <table className="bdc-t bdc-pv">
            <thead>
              <tr>
                <th colSpan={width} className="title">
                  {title} · สรุปทั้ง PO
                </th>
              </tr>
              <ColumnHead columns={sheet.columns} />
            </thead>
            <tbody>
              <TotalRows sheet={sheet} />
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <table className="bdc-t bdc-pv">
      <thead>
        <ColumnHead columns={sheet.columns} />
      </thead>
      {sheet.lines.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={width} className="c sub">
              ไม่พบร้านที่ค้นหา
            </td>
          </tr>
        </tbody>
      ) : (
        sheet.lines.map((line) => (
          <tbody key={String(line.line_no)}>
            <tr className="lh">
              <td colSpan={width}>
                สาย {lineText(line.line_no)} · {line.rows.length} ร้าน
              </td>
            </tr>
            {line.rows.map((row) => (
              <tr key={row.store_code}>
                <td className="q">{lineText(row.stop_no)}</td>
                <td className="mono">{row.store_code}</td>
                <td>{row.store_name}</td>
                {row.cells.map(qtyCell)}
                <td className="q">
                  <b>{row.total}</b>
                </td>
              </tr>
            ))}
            <tr className="ls">
              <td colSpan={3}>รวมสาย {lineText(line.line_no)}</td>
              {line.subtotal.map(qtyCell)}
              <td className="q">{pcs(line.total)}</td>
            </tr>
          </tbody>
        ))
      )}
      {sheet.lines.length > 0 && (
        <tbody>
          <TotalRows sheet={sheet} />
        </tbody>
      )}
    </table>
  );
}

export default memo(ControlSheetView);
